<script lang="ts">
import RichText from '$components/Card/RichText.svelte';
import { shuffle } from '$lib/exam';
import {
  SPRINT_DURATION_MS,
  SPRINT_TARGET_MS,
  type SprintEntry,
  type SprintSummary,
  sprintSummary,
} from '$lib/sprint';
import type { Card } from '$types/card';

interface Props {
  cards: readonly Card[];
}

const { cards }: Props = $props();

const pool = $derived(cards.filter((c) => c.deck === 'tabellenbuch'));

let phase = $state<'setup' | 'run' | 'done'>('setup');
let queue = $state<Card[]>([]);
let idx = $state(0);
let entries = $state<SprintEntry[]>([]);
let revealed = $state(false);
let cardStartMs = $state(0);
let nowMs = $state(0);
let summary = $state<SprintSummary | null>(null);

let sprintStartMs = $state(0);
const current = $derived(queue[idx] ?? null);
const remainingMs = $derived(Math.max(0, SPRINT_DURATION_MS - (nowMs - sprintStartMs)));
const elapsedCardSec = $derived(revealed ? 0 : Math.floor((nowMs - cardStartMs) / 1000));

function start(): void {
  queue = shuffle(pool);
  idx = 0;
  entries = [];
  revealed = false;
  sprintStartMs = Date.now();
  cardStartMs = sprintStartMs;
  nowMs = sprintStartMs;
  phase = 'run';
}

function finish(): void {
  summary = sprintSummary(entries);
  phase = 'done';
}

function found(): void {
  if (!current) return;
  entries = [...entries, { cardId: current.id, ms: Date.now() - cardStartMs }];
  revealed = true;
}

function next(): void {
  revealed = false;
  if (idx + 1 >= queue.length) {
    finish();
    return;
  }
  idx++;
  cardStartMs = Date.now();
}

$effect(() => {
  if (phase !== 'run') return;
  const t = setInterval(() => {
    nowMs = Date.now();
    if (nowMs - sprintStartMs >= SPRINT_DURATION_MS) finish();
  }, 250);
  return () => clearInterval(t);
});

function mmss(ms: number): string {
  const sec = Math.ceil(ms / 1000);
  return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
}
</script>

<section class="sprint">
  {#if phase === 'setup'}
    <div class="rule"><span class="rule-label">[ TABELLENBUCH-SPRINT ]</span><span class="rule-line"></span></div>
    <p class="hint">
      5 Minuten, echtes Tabellenbuch neben dich. Begriff erscheint → im Buch nachschlagen →
      <b>gefunden</b> drücken → Fundstelle vergleichen. Ziel: <b>&lt; 30 s pro Begriff</b>
      (Prüfungs-Tempo). {pool.length} Begriffe im Pool.
    </p>
    <button type="button" class="btn-start" onclick={start} disabled={pool.length === 0}>▶ 5-min-sprint starten</button>
  {:else if phase === 'run' && current}
    <div class="rule">
      <span class="rule-label">[ BEGRIFF {idx + 1}/{queue.length} · {entries.length} gefunden ]</span>
      <span class="rule-line"></span>
      <span class="timer">⏱ {mmss(remainingMs)}</span>
    </div>
    <div class="term-card">
      <div class="term"><RichText content={'front' in current ? (current.front ?? '') : ''} /></div>
      {#if !revealed}
        <div class="stopwatch" class:over={elapsedCardSec >= 30}>{elapsedCardSec} s</div>
        <button type="button" class="btn-start" onclick={found}>✓ gefunden</button>
      {:else}
        <div class="answer">
          <div class="answer-label">↳ fundstelle (abgleichen!)</div>
          <RichText content={'back' in current ? (current.back ?? '') : ''} />
        </div>
        <button type="button" class="btn-start" onclick={next}>weiter →</button>
      {/if}
    </div>
  {:else if phase === 'done' && summary}
    <div class="rule"><span class="rule-label">[ SPRINT-ERGEBNIS ]</span><span class="rule-line"></span></div>
    <div class="result">
      <div class="res-row"><span>begriffe gefunden</span><b>{summary.count}</b></div>
      <div class="res-row"><span>Ø zeit pro begriff</span><b>{(summary.avgMs / 1000).toFixed(1)} s</b></div>
      <div class="res-row">
        <span>unter 30-s-ziel</span>
        <b class:good={summary.underTargetPct >= 0.8}>{summary.underTarget}/{summary.count} ({Math.round(summary.underTargetPct * 100)} %)</b>
      </div>
      <p class="hint">
        {#if summary.count === 0}Kein Begriff geschafft — nochmal!{:else if summary.avgMs < SPRINT_TARGET_MS}
          Prüfungs-Tempo erreicht. Morgen wieder 5 Minuten.
        {:else}Noch über dem 30-s-Ziel — Register-Logik üben (Stichwortverzeichnis zuerst).{/if}
      </p>
    </div>
    <button type="button" class="btn-start" onclick={() => (phase = 'setup')}>↺ nochmal</button>
  {/if}
</section>

<style>
  .sprint { margin-top: 8px; }
  .rule {
    display: flex; align-items: center; gap: 10px;
    margin: 16px 0 8px;
    font-size: 11px; letter-spacing: 0.08em;
    color: var(--ink-3); text-transform: uppercase;
  }
  .rule-label { white-space: nowrap; font-weight: 600; }
  .rule-line { flex: 1; height: 0; border-top: 1px dashed var(--rule); opacity: 0.5; }
  .hint { font-size: 13px; color: var(--ink-2); }
  .timer { font-weight: 700; font-variant-numeric: tabular-nums; }

  .btn-start {
    border: 2px solid var(--ink); background: var(--ink); color: var(--paper);
    padding: 10px 18px; font-family: var(--font-mono); font-size: 12px;
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; cursor: pointer;
    margin-top: 10px;
  }
  .btn-start:hover { background: var(--accent); border-color: var(--accent); }
  .btn-start:disabled { opacity: 0.4; cursor: not-allowed; }

  .term-card {
    border: 2px solid var(--ink); background: var(--paper);
    padding: 18px; box-shadow: 6px 6px 0 var(--ink-3);
  }
  .term { font-size: 20px; font-weight: 600; }
  .stopwatch {
    font-size: 42px; font-weight: 800; font-variant-numeric: tabular-nums;
    color: var(--ink); margin: 10px 0 4px;
  }
  .stopwatch.over { color: var(--r-again, #c62828); }
  .answer { margin-top: 12px; padding: 10px; background: var(--paper-2); border-left: 4px solid var(--accent); }
  .answer-label {
    font-size: 10px; letter-spacing: 0.12em; color: var(--accent-2);
    text-transform: uppercase; font-weight: 700; margin-bottom: 6px;
  }

  .result { border: 2px solid var(--ink); padding: 16px; box-shadow: 6px 6px 0 var(--ink-3); }
  .res-row {
    display: flex; justify-content: space-between; padding: 8px 0;
    border-bottom: 1px dashed var(--rule);
    font-size: 13px; text-transform: uppercase; letter-spacing: 0.06em;
  }
  .res-row b { font-variant-numeric: tabular-nums; }
  .res-row b.good { color: var(--r-good, #2e7d32); }
</style>
